/**
 * Contract Service
 * Pure functions for contract-related API calls
 */

import apiClient from '../api/client';

/**
 * Get contract status by public token
 * @param {string} token - Public contract token
 * @returns {Promise<{data: {exists: boolean, status: string, clientName: string, clientPhoneMasked: string}}>}
 */
export const getContractStatus = async (token) => {
  return apiClient.get(`/public/contracts/${token}/status`, {
    requiresAuth: false,
  });
};

/**
 * Send OTP code to phone number
 * @param {string} token - Public contract token
 * @param {string} phone - Phone number to send OTP to
 * @returns {Promise<{data: {sent: boolean, expiresAt: string, _devOtp?: string}}>}
 */
export const sendOtp = async (token, phone) => {
  return apiClient.post(
    `/public/contracts/${token}/otp/send`,
    { phone },
    { requiresAuth: false }
  );
};

/**
 * Verify OTP code
 * @param {string} token - Public contract token
 * @param {string} phone - Phone number
 * @param {string} code - OTP code
 * @returns {Promise<{data: {verified: boolean, token: string}}>}
 */
export const verifyOtp = async (token, phone, code) => {
  return apiClient.post(
    `/public/contracts/${token}/otp/verify`,
    { phone, code },
    { requiresAuth: false }
  );
};

/**
 * Get current contract details (requires JWT)
 * @returns {Promise<{data: {id: string, status: string, clientName: string, contentHtml: string, ...}}>}
 */
export const getMyContract = async () => {
  return apiClient.get('/public/contracts/me');
};

/**
 * Get contract content by admin view token (read-only, no OTP)
 * @param {string} token - Admin view JWT from view-link
 * @returns {Promise<{data: { contentHtml, isAdminView, ... }}>}
 */
export const getContractByAdminToken = async (token) => {
  return apiClient.get(`/public/contracts/${encodeURIComponent(token)}/admin-content`, {
    requiresAuth: false,
  });
};

/**
 * Sign the contract (requires JWT)
 * @param {string} signature - Base64 signature image data URL
 * @param {Object} checkboxStates - Object with checkbox states {[id]: boolean}
 * @returns {Promise<{data: {signed: boolean, signedAt: string, contractId: string, finalHash: string}}>}
 */
export const signContract = async (signature, checkboxStates) => {
  return apiClient.post('/public/contracts/me/sign', {
    signature,
    checkboxStates,
  });
};
